import React, { Fragment, useMemo, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  AutoComplete,
  FormInput,
  ModalWrapper,
  TextField,
} from '@microservices/wiskey-react-components'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import { Alert, Box, Button, Divider, IconButton, SxProps, Typography } from '@mui/material'

import { SearchFilterValue } from '@pages/SearchAssistantsCreateOrEdit/types'

import { FieldPickerController } from '@components/hookFormControllers/FieldPickerController'

import { useFetchAllObjectQuery } from '@redux/api'

import { FIELD_VALUE_TYPE, GENERATOR_INPUT_TYPE, MIN_LABEL_WIDTH } from '@constants'
import { AutocompleteOption, EntityCommandDTO } from '@types'

import { ConfigField, EmbeddedObject } from '@gantt/components/GanttCreateOrEdit'
import { OptionsFilter } from '@gantt/types'

export type ValuePicker = {
  pathStr: string
  pathArray: EmbeddedObject[]
  field: string
  commands: EntityCommandDTO[]
}

type ModalRefPathPickerProps = {
  isFieldArraySingleItem?: boolean
  isDisabled: boolean
  isRequired?: boolean
  isEdit?: boolean
  isFlatOptions?: boolean
  hasClearInInput?: boolean
  hasField?: boolean
  hasCommands?: boolean
  name: string
  label?: string
  embeddedObjectPickerControllerName: string
  pickerName: string
  placeHolder?: string
  sx?: SxProps
  fieldName?: string
  commandsName?: string
  prefix?: string
  suffix?: string
  object?: AutocompleteOption<string> | null
  refObjectOptions?: AutocompleteOption[]
  currentValue?: ValuePicker | SearchFilterValue
  onChange?: () => void
  onSave?: (value: ConfigField) => void
  onRemove?: () => void
  onChangeRootObject?: (option: AutocompleteOption<string>) => void
  optionsFilter?: OptionsFilter
  existingPaths?: string[]
  selectedPaths?: string[]
  sxPickButton?: SxProps
  formInputContainerSx?: SxProps
  refPickAllowed?: boolean
  labelPlacement?: 'bottom' | 'top' | 'end' | 'start'
  labelSx?: SxProps
  warningHint?: string
}

//todo почему не в src/components/ ?
export const ModalRefPathPicker = ({
  isFieldArraySingleItem = false,
  isDisabled,
  refPickAllowed = false,
  isEdit = false,
  isFlatOptions,
  isRequired = false,
  hasField = false,
  hasClearInInput = false,
  name,
  label,
  labelPlacement = undefined,
  embeddedObjectPickerControllerName,
  pickerName,
  placeHolder,
  sx,
  fieldName,
  prefix = '',
  suffix = '',
  object,
  onChange,
  onChangeRootObject,
  onSave,
  onRemove,
  optionsFilter,
  existingPaths,
  selectedPaths,
  sxPickButton,
  formInputContainerSx,
  refObjectOptions,
  labelSx,
  warningHint,
}: ModalRefPathPickerProps) => {
  const { t } = useTranslation()

  const {
    watch: outerWatch,
    setValue: outerSetValue,
    getValues: outerGetValue,
    trigger: outerTrigger,
  } = useFormContext()

  const outerPath = outerWatch(name)
  const outerPathArray = outerWatch(embeddedObjectPickerControllerName)

  const methods = useForm<{ path: string; pathArray: EmbeddedObject[] }>({
    defaultValues: {
      pathArray: structuredClone(outerPathArray),
    },
  })

  const [show, setShow] = useState(false)

  const {
    watch,
    setValue,
    resetField,
    trigger,
    reset,
    formState: { isDirty },
  } = methods

  const pathArrayFieldName = 'pathArray'

  const watchedPathArray: EmbeddedObject[] = watch(pathArrayFieldName)

  const {
    data: objects,
    isLoading: isLoadingObjects,
    isFetching: isFetchingObjects,
  } = useFetchAllObjectQuery(undefined, {
    skip: !show,
  })

  const loadingObjects = isLoadingObjects || isFetchingObjects

  const objectOptions: AutocompleteOption[] = useMemo(() => {
    if (refObjectOptions?.length) {
      return refObjectOptions
    }

    return objects?.map(obj => ({ id: obj.code, label: obj.title })) || []
  }, [objects])

  const handleClear = () => {
    outerSetValue(name, '', { shouldDirty: true })
    fieldName && outerSetValue(fieldName, '', { shouldDirty: true })
    outerSetValue(embeddedObjectPickerControllerName, [], { shouldDirty: true })

    setValue(pathArrayFieldName, [])
    onRemove?.()
  }

  const getRefPath = (embeddedObjects: EmbeddedObject[], name: string) => {
    if (embeddedObjects) {
      const path = embeddedObjects
        .map(obj => obj.field)
        .filter(item => item)
        .join('.')

      outerSetValue(name, path, { shouldValidate: true, shouldDirty: true })

      return path
    }
  }

  const getFieldName = (fieldName: string, fieldValue: string, prefix?: string) => {
    const fieldPlace = [prefix, fieldValue, suffix].filter(s => !!s).join('_')

    return outerSetValue(`${fieldName}`, fieldPlace, { shouldValidate: true })
  }

  const getFieldVal = () => {
    return watchedPathArray
      ?.map(obj => obj.field)
      .filter(item => item)
      .join('')
  }

  const handleSave = () => {
    trigger().then(value => {
      if (value) {
        outerTrigger(embeddedObjectPickerControllerName).then(value => {
          if (value) {
            getRefPath(watchedPathArray, name)

            if (fieldName && hasField) {
              getFieldName(fieldName, getFieldVal(), prefix)
            }

            outerSetValue(
              embeddedObjectPickerControllerName,
              watchedPathArray.filter(obj => obj.field),
              { shouldValidate: true, shouldDirty: true }
            )

            setShow(false)
            onSave?.(outerGetValue(pickerName))
          }
          onChange?.()
        })
      }
    })
  }

  const handleOpenModal = () => {
    reset({ pathArray: structuredClone(outerPathArray) })

    setShow(true)
  }

  const handleCloseModal = () => {
    resetField(pathArrayFieldName)

    setShow(false)
  }

  const handleChangeDefaultObject = (value: AutocompleteOption<string>) => {
    outerSetValue(name, '', { shouldDirty: true })
    fieldName && outerSetValue(fieldName, '', { shouldDirty: true })
    outerSetValue(
      embeddedObjectPickerControllerName,
      [{ objectCode: value?.id, refValueType: FIELD_VALUE_TYPE.OBJ_PK_LINK, field: null }],
      { shouldDirty: true }
    )

    onChangeRootObject?.(value)
  }

  return (
    <Fragment key={String(name)}>
      {Boolean(warningHint) && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          {warningHint}
        </Alert>
      )}
      <Box alignItems='center' display='flex' gap={'0 5px'} justifyContent='space-between' sx={sx}>
        <Box flex={1}>
          <FormInput
            formInputContainerSx={formInputContainerSx}
            inputType={GENERATOR_INPUT_TYPE.INPUT}
            label={label}
            labelPlacement={labelPlacement}
            labelSx={labelSx}
            name={name}
            placeholder={placeHolder ?? t('placeholder.refPath')}
            readOnly={true}
            rules={{
              required: isRequired,
            }}
            textFieldInputProps={{
              ...(hasClearInInput && {
                endAdornment: (
                  <IconButton
                    disabled={!outerPath}
                    size='small'
                    sx={{ width: 36.5, py: '10px' }}
                    onClick={handleClear}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                ),
              }),
            }}
          />
        </Box>
        <Box display='flex' mb={1}>
          <Button
            disabled={isDisabled}
            variant='contained'
            sx={{
              margin: '0 15px',
              ...(sxPickButton ? sxPickButton : {}),
            }}
            onClick={handleOpenModal}
          >
            {outerPath ? t('ganttCreate.refEdit') : t('ganttCreate.refPick')}
          </Button>
          {!hasClearInInput && (
            <IconButton size='small' sx={{ width: 35 }} onClick={handleClear}>
              {isFieldArraySingleItem && <ClearIcon fontSize='small' />}
              {!isFieldArraySingleItem && <DeleteIcon fontSize='small' />}
            </IconButton>
          )}
        </Box>
        {hasField && fieldName && (
          <Box hidden>
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              name={fieldName}
              placeholder={placeHolder ?? t('placeholder.refPath')}
              readOnly={true}
              sx={{ margin: 0 }}
            />
          </Box>
        )}
      </Box>
      {show && (
        <FormProvider {...methods}>
          <ModalWrapper
            btnText={t('ganttCreate.common.apply')}
            disabledSave={!isDirty}
            isShow={show}
            onClose={handleCloseModal}
            onSave={handleSave}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
              <Typography
                sx={{
                  width: MIN_LABEL_WIDTH,
                  minWidth: MIN_LABEL_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('label.defaultObject')}
              </Typography>
              <AutoComplete
                disableClearable
                disabled={!onChangeRootObject}
                loading={loadingObjects}
                options={objectOptions}
                size='small'
                sx={{ width: '100%' }}
                value={object}
                renderInput={params => {
                  return (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps }}
                      placeholder={t('placeholder.defaultObject')}
                      variant='outlined'
                    />
                  )
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )
                }}
                onChange={handleChangeDefaultObject}
              />
            </Box>
            <Box display='flex' mt={1}>
              <Box width={120}>{t('ganttCreate.modalPathPickerObjectsColumn')}</Box>
              <Divider flexItem orientation='vertical' />
              <Box pl={1}>{t('ganttCreate.modalPathPickerFieldsColumn')}</Box>
            </Box>
            <Box py='15px'>
              <FieldPickerController
                existingPaths={existingPaths}
                isEdit={isEdit}
                isFlatOptions={isFlatOptions}
                name={pathArrayFieldName}
                object={object}
                optionsFilter={optionsFilter}
                refPickAllowed={refPickAllowed}
                selectedPaths={selectedPaths}
              />
            </Box>
          </ModalWrapper>
        </FormProvider>
      )}
    </Fragment>
  )
}
